if (!customElements.get('fa-slider')) {
  class FASlider extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.origSlides = [];
      this.slides = [];
      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this._horizontalDrag = false;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.autoplayInterval = null;
      this.pointerId = null;
      this._transitioning = false;
      this._cachedSlideWidth = 0;
      this._transitionTimer = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[class*="__track"]');
      if (!this.track) return;

      this.origSlides = Array.from(this.track.children);
      const n = this.origSlides.length;
      if (n === 0) return;

      // Force all original slide images to load immediately
      this.origSlides.forEach(function(slide) {
        slide.querySelectorAll('img').forEach(function(img) { img.loading = 'eager'; });
      });

      // Prepend clones of all slides (in order: S1c S2c ... SNc)
      for (let i = n - 1; i >= 0; i--) {
        const clone = this.origSlides[i].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('img[loading="lazy"]').forEach(function(img) { img.loading = 'eager'; });
        this.track.insertBefore(clone, this.track.firstChild);
      }

      // Append clones of all slides (in order: S1c S2c ... SNc)
      for (let i = 0; i < n; i++) {
        const clone = this.origSlides[i].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('img[loading="lazy"]').forEach(function(img) { img.loading = 'eager'; });
        this.track.appendChild(clone);
      }

      // Full slide list: [clones-pre | originals | clones-post]
      // Indices:          0..(n-1)  | n..(2n-1) | 2n..(3n-1)
      this.slides = Array.from(this.track.children);
      this.currentIndex = n; // start at first real slide

      this.track.style.touchAction = 'pan-y';

      /* Block page scroll only when a horizontal drag is confirmed */
      var self = this;
      this.track.addEventListener('touchmove', function(e) {
        if (self._horizontalDrag) e.preventDefault();
      }, { passive: false });

      this.prevBtn = this.querySelector('[data-prev]') || this.parentElement.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]') || this.parentElement.querySelector('[data-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.prev.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.next.bind(this));

      this.onDragStart = this.onDragStart.bind(this);
      this.onDragMove = this.onDragMove.bind(this);
      this.onDragEnd = this.onDragEnd.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);

      this.track.addEventListener('pointerdown', this.onDragStart);
      this.track.addEventListener('pointermove', this.onDragMove);
      this.track.addEventListener('pointerup', this.onDragEnd);
      this.track.addEventListener('pointercancel', this.onDragEnd);
      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      this.track.addEventListener('dragstart', function(e) { e.preventDefault(); });

      window.addEventListener('resize', this.handleResize);
      document.addEventListener('visibilitychange', this.handleVisibility);
      window.addEventListener('pageshow', this.handleVisibility);

      // Defer layout reads to after initial paint to avoid forced reflow
      requestAnimationFrame(() => {
        this._cacheSlideWidth();
        this.goTo(this.currentIndex, false);
        var self = this;
        requestAnimationFrame(function() {
          self.classList.add('is-ready');
          // Autoplay starts only when section enters viewport — not on page load
        });
      });

      // Start autoplay only when section is visible in viewport
      var self = this;
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              self.setupAutoplay();
            } else {
              self.stopAutoplay();
            }
          });
        }, { threshold: 0.2 });
        this._io.observe(this);
      } else {
        // Fallback for browsers without IntersectionObserver
        self.setupAutoplay();
      }
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this.stopAutoplay();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      window.removeEventListener('pageshow', this.handleVisibility);
    }

    _cacheSlideWidth() {
      // Always measure an inactive slide so the active slide's expanded size doesn't skew the value
      var sample = null;
      for (var i = 0; i < this.slides.length; i++) {
        if (!this.slides[i].classList.contains('is-active')) { sample = this.slides[i]; break; }
      }
      if (!sample) sample = this.slides[0];
      if (!sample) { this._cachedSlideWidth = 0; return; }
      var gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this._cachedSlideWidth = sample.offsetWidth + gap;
    }

    getSlideWidth() {
      return this._cachedSlideWidth;
    }

    _updateActiveClass(index) {
      this.slides.forEach(function(slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
    }

    goTo(index, animate) {
      if (animate === undefined) animate = true;
      this.currentIndex = index;
      this.currentTranslate = -index * this.getSlideWidth();
      this.prevTranslate = this.currentTranslate;
      if (animate) {
        this._updateActiveClass(index);
        this.track.style.transition = 'transform 2s cubic-bezier(0.4, 0, 0.2, 1)';
        this._transitioning = true;
        clearTimeout(this._transitionTimer);
        var self = this;
        this._transitionTimer = setTimeout(function() {
          if (self._transitioning) {
            self.handleTransitionEnd({ target: self.track });
          }
        }, 2200);
      } else {
        var self = this;
        this.slides.forEach(function(s) { s.style.transition = 'none'; });
        this._updateActiveClass(index);
        this.track.style.transition = 'none';
        void this.track.offsetWidth;
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            self.slides.forEach(function(s) { s.style.transition = ''; });
          });
        });
      }
      this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
    }

    handleTransitionEnd(e) {
      // Slide width/height transitions bubble up — only handle the track's own transform
      if (e && e.target !== this.track) return;
      clearTimeout(this._transitionTimer);
      var n = this.origSlides.length;
      var jump = null;
      if (this.currentIndex < n) {
        jump = this.currentIndex + n;
      } else if (this.currentIndex >= 2 * n) {
        jump = this.currentIndex - n;
      }
      if (jump !== null) {
        this.currentIndex = jump;
        var self = this;
        this.slides.forEach(function(s) { s.style.transition = 'none'; });
        this._updateActiveClass(jump);
        this.currentTranslate = -jump * this.getSlideWidth();
        this.prevTranslate = this.currentTranslate;
        this.track.style.transition = 'none';
        void this.track.offsetWidth;
        this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
        /* Double rAF: first frame calculates layout, second paints it,
           then re-enable transitions so scale animation doesn't restart */
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            self.slides.forEach(function(s) { s.style.transition = ''; });
            self._transitioning = false;
          });
        });
      } else {
        this._transitioning = false;
      }
    }

    next() {
      if (this._transitioning) return;
      this.goTo(this.currentIndex + 1);
    }

    prev() {
      if (this._transitioning) return;
      this.goTo(this.currentIndex - 1);
    }

    onDragStart(e) {
      if (this._transitioning) return;
      this.isDragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this._horizontalDrag = false;
      this.prevTranslate = this.currentTranslate;
      this.track.style.transition = 'none';
      this.stopAutoplay();
      this.track.setPointerCapture(e.pointerId);
      this.pointerId = e.pointerId;
    }

    onDragMove(e) {
      if (!this.isDragging) return;
      var diffX = e.clientX - this.startX;
      var diffY = e.clientY - this.startY;
      if (!this._horizontalDrag) {
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 5) {
          this._horizontalDrag = true;
        } else if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffY) > 5) {
          /* Vertical scroll intent — cancel horizontal drag, let page scroll */
          this.isDragging = false;
          this.currentTranslate = this.prevTranslate;
          this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
          return;
        } else {
          return; /* Too small to determine direction yet */
        }
      }
      this.currentTranslate = this.prevTranslate + diffX;
      this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
    }

    onDragEnd(_e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this._horizontalDrag = false;

      if (this.pointerId !== null) {
        try { this.track.releasePointerCapture(this.pointerId); } catch(ex) {}
        this.pointerId = null;
      }

      var moved = this.currentTranslate - this.prevTranslate;
      var threshold = this.getSlideWidth() * 0.15;

      if (Math.abs(moved) > threshold) {
        if (moved < 0) {
          this.next();
        } else {
          this.prev();
        }
      } else {
        this.goTo(this.currentIndex);
      }

      this.setupAutoplay();
    }

    setupAutoplay() {
      this.stopAutoplay();
      if (this.getAttribute('data-autoplay') !== 'true') return;
      var speed = parseInt(this.getAttribute('data-speed'), 10) || 4;
      var self = this;
      this.autoplayInterval = setInterval(function() {
        self.next();
      }, speed * 1000);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    handleResize() {
      this._cacheSlideWidth();
      this.goTo(this.currentIndex, false);
    }

    handleVisibility() {
      if (document.visibilityState === 'hidden') {
        this.stopAutoplay();
        return;
      }
      var self = this;
      requestAnimationFrame(function() {
        self.stopAutoplay();
        clearTimeout(self._transitionTimer);
        self._transitioning = false;

        // Strip all inline transitions and is-active so we measure a clean inactive width
        self.slides.forEach(function(s) {
          s.style.transition = 'none';
          s.classList.remove('is-active');
        });
        self.track.style.transition = 'none';
        void self.track.offsetWidth; // force reflow with all slides at inactive size

        self._cacheSlideWidth();

        // Re-apply active class and snap track to correct position
        self._updateActiveClass(self.currentIndex);
        self.currentTranslate = -self.currentIndex * self.getSlideWidth();
        self.prevTranslate = self.currentTranslate;
        self.track.style.transform = 'translateX(' + self.currentTranslate + 'px)';

        // Re-enable transitions and restart autoplay next frame
        requestAnimationFrame(function() {
          self.slides.forEach(function(s) { s.style.transition = ''; });
          self.setupAutoplay();
        });
      });
    }
  }

  customElements.define('fa-slider', FASlider);
}
